import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft, Loader2, Sparkles, Flame, Cross, BookOpen, ScrollText, RotateCcw,
  CheckCircle2, PartyPopper,
} from 'lucide-react';
import { toast } from 'sonner';
import Confetti from 'react-confetti';
import LikeButton from '@/components/LikeButton';

const CATEGORY_META = {
  OT: { label: 'Old Testament', icon: ScrollText, color: 'from-amber-500 to-orange-500', chip: 'bg-amber-100 text-amber-800' },
  GOSPEL: { label: 'Gospels', icon: Cross, color: 'from-rose-500 to-pink-500', chip: 'bg-rose-100 text-rose-800' },
  NT: { label: 'New Testament', icon: BookOpen, color: 'from-indigo-500 to-purple-500', chip: 'bg-indigo-100 text-indigo-800' },
};

const BibleJourneyPage = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confetti, setConfetti] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/bible-journey/today/${childId}`);
      setToday(res.data);
    } catch (err) {
      toast.error('Could not load your Bible Journey plan.');
    } finally {
      setLoading(false);
    }
  }, [api, childId]);

  useEffect(() => { load(); }, [load]);

  const completeToday = async () => {
    if (!today || today.already_completed_today) return;
    setSaving(true);
    try {
      const res = await api.post(`/bible-journey/complete/${childId}`);
      if (!res.data.already_completed) {
        setConfetti(true);
        toast.success(`Day ${today.day} complete! Streak: ${res.data.streak}`);
        setTimeout(() => setConfetti(false), 4000);
      } else {
        toast.info('Already marked complete for today.');
      }
      await load();
    } catch (err) {
      toast.error('Could not save progress.');
    } finally {
      setSaving(false);
    }
  };

  const resetPlan = async () => {
    if (!window.confirm('Reset your Bible Journey plan back to Day 1? Your streak will restart.')) return;
    try {
      await api.post(`/bible-journey/reset/${childId}`);
      toast.success('Plan reset. Off to a fresh start!');
      await load();
    } catch (err) {
      toast.error('Could not reset the plan.');
    }
  };

  const openStory = (storyId) => {
    // BibleStoryActivity route
    navigate(`/bible-story/${storyId}?child=${childId}`);
  };

  const dayProgress = today ? Math.min(100, Math.round((today.day / today.total_days) * 100)) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-rose-50">
      {confetti && <Confetti recycle={false} numberOfPieces={220} />}

      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/learn/${childId}`)} data-testid="bible-journey-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <Link to={`/learn/${childId}`} className="inline-block hover:opacity-70 transition-opacity" data-testid="bible-journey-title-link">
                <h1 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" /> Bible Journey
                </h1>
              </Link>
              <p className="text-xs text-slate-500">90-day guided reading plan — one story from each part of the Bible, every day.</p>
            </div>
          </div>
          {today && (
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs text-slate-500">Streak</p>
                <p className="text-2xl font-extrabold text-orange-600 flex items-center gap-1">
                  <Flame className="w-5 h-5" />{today.streak}d
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Day</p>
                <p className="text-2xl font-extrabold text-amber-700" data-testid="bible-journey-day">
                  {today.day} <span className="text-slate-400 text-sm">/ {today.total_days}</span>
                </p>
              </div>
            </div>
          )}
        </div>
        {today && (
          <div className="max-w-4xl mx-auto px-4 pb-3">
            <Progress value={dayProgress} className="h-2 bg-amber-100" />
          </div>
        )}
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {loading && (
          <div className="py-24 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        )}

        {!loading && today && (
          <>
            {today.already_completed_today && (
              <Card className="border-2 border-emerald-300 bg-emerald-50">
                <CardContent className="p-4 flex items-center gap-3 justify-center" data-testid="bible-journey-done-banner">
                  <PartyPopper className="w-6 h-6 text-emerald-600" />
                  <p className="font-semibold text-emerald-800">Today&apos;s reading complete! Come back tomorrow for Day {today.day + 1 <= today.total_days ? today.day + 1 : today.day}.</p>
                </CardContent>
              </Card>
            )}

            <div className="grid gap-4" data-testid="bible-journey-cards">
              {(today.stories || []).map((s) => {
                const meta = CATEGORY_META[s.category] || CATEGORY_META.OT;
                const Icon = meta.icon;
                return (
                  <Card key={s.id} className="overflow-hidden border-slate-200 hover:shadow-md transition-all">
                    <div className={`bg-gradient-to-r ${meta.color} px-5 py-3 flex items-center justify-between text-white`}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-5 h-5" />
                        <span className="font-semibold">{meta.label}</span>
                      </div>
                      <Badge className="bg-white/20 text-white border-0 text-xs">{s.reference}</Badge>
                    </div>
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-lg font-extrabold text-slate-900">{s.title}</h3>
                        <LikeButton
                          childId={childId}
                          contentType="bible_story"
                          contentId={s.id}
                          size="md"
                          metadata={{ title: s.title, reference: s.reference, category: s.category, preview: s.summary }}
                          testIdPrefix="bible-journey-like"
                        />
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{s.summary}</p>
                      <div className={`text-xs italic rounded-md px-3 py-2 ${meta.chip}`}>
                        <span className="font-semibold">Big idea:</span> {s.lesson}
                      </div>
                      <div className="flex gap-2 flex-wrap pt-1">
                        <Button
                          size="sm"
                          onClick={() => openStory(s.id)}
                          className="bg-slate-900 hover:bg-slate-800 text-white"
                          data-testid={`bible-journey-read-${s.id}`}
                        >
                          <BookOpen className="w-4 h-4 mr-1" />
                          Read story
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="flex gap-3 flex-wrap">
              <Button
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 py-6 text-base font-bold"
                disabled={saving || today.already_completed_today}
                onClick={completeToday}
                data-testid="bible-journey-complete"
              >
                {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                {today.already_completed_today ? `Today's reading complete` : `Mark Day ${today.day} complete`}
              </Button>
              <Button variant="outline" onClick={resetPlan} data-testid="bible-journey-reset">
                <RotateCcw className="w-4 h-4 mr-1" />
                Restart
              </Button>
            </div>
          </>
        )}

        {!loading && !today && (
          <Card><CardContent className="p-8 text-center text-slate-500">
            No plan available. Please try again later.
          </CardContent></Card>
        )}
      </main>
    </div>
  );
};

export default BibleJourneyPage;
